import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { useKeyHandoverServices } from "../context/KeyHandoverServiceContext";
import { parseBookingQuery } from "../common/url/bookingQuery";
import type { RootState } from "../../../../redux/store";
import type { EquipmentListProps } from "../../../../types/baseTypes";

/**
 * Custom hook to hydrate service selection from URL and sync back to URL.
 *
 * @remarks
 * - Must be called within KeyHandoverServiceProvider context
 * - Only runs once per mount
 * - Syncs serviceId back to URL after selection changes
 * - Returns hydration status
 *
 * @returns boolean - Whether service hydration is complete
 *
 * @example
 * ```tsx
 * function ServiceStep() {
 *   const serviceHydrated = useServiceHydration();
 *   if (!serviceHydrated) return <Loading />;
 *   // Service now hydrated from URL and synced
 * }
 * ```
 */
export function useServiceHydration(): boolean {
  const location = useLocation();
  const navigate = useNavigate();

  const { services, selected: selectedService, select: selectService } = useKeyHandoverServices();
  const selectedLocation = useSelector(
    (state: RootState) => state.bookingCreation.selectedLocation
  );
  const selectedGroup = useSelector((state: RootState) => state.bookingCreation.selectedGroup);

  const serviceHydratedRef = useRef(false);
  const initialServiceIdRef = useRef<string | null>(null);
  const initialLocationIdRef = useRef<string | null>(null);
  const lastWrittenServiceIdRef = useRef<string | null>(null);
  const capturedRef = useRef(false);
  const [isHydrated, setIsHydrated] = useState(false);

  // Capture initial query parameters once on mount
  useEffect(() => {
    if (capturedRef.current) return;

    const query = parseBookingQuery(location.search);
    initialServiceIdRef.current = query.serviceId ?? null;
    initialLocationIdRef.current = query.locationId ?? null;
    capturedRef.current = true;
  }, [location.search]);

  // Hydrate service from URL
  useEffect(() => {
    if (serviceHydratedRef.current) return;
    
    const svcId = initialServiceIdRef.current;
    const desiredLocId = initialLocationIdRef.current;

    // If no serviceId in URL, mark as hydrated immediately (nothing to hydrate)
    if (!svcId) {
      serviceHydratedRef.current = true;
      setIsHydrated(true);
      return;
    }

    // If we need to hydrate a service, wait for services to load
    if (!services || services.length === 0) return;

    // Ensure location matches if specified
    const locationMatches = !desiredLocId || selectedLocation?.id === desiredLocId;
    if (!locationMatches) {
      // Set timeout: if location doesn't match after 3 seconds, proceed anyway
      const timeoutId = setTimeout(() => {
        if (!serviceHydratedRef.current) {
          console.warn(
            `[useServiceHydration] Location hydration timeout - expected ${desiredLocId}, got ${selectedLocation?.id}. Proceeding anyway.`
          );
          serviceHydratedRef.current = true;
          setIsHydrated(true);
        }
      }, 3000);
      return () => clearTimeout(timeoutId);
    }

    // Try to find and select the service from URL
    const found = services.find((s: EquipmentListProps) => s.id === svcId);
    if (found && (!selectedService || selectedService.id !== svcId)) {
      selectService(found);
    } else if (!found) {
      console.warn(`[useServiceHydration] Service with id ${svcId} not found in available services`);
    }

    serviceHydratedRef.current = true;
    setIsHydrated(true);
  }, [services, selectedService, selectService, selectedLocation]);

  // Sync serviceId to URL after hydration
  useEffect(() => {
    if (!serviceHydratedRef.current) return; // Wait for initial hydration

    const newId = selectedGroup?.id ?? null;
    if (!newId) return; // Don't remove serviceId to avoid loops

    if (lastWrittenServiceIdRef.current === newId) return; // Prevent redundant writes

    const params = new URLSearchParams(location.search);
    const currentId = params.get("serviceId");

    if (currentId === newId) {
      lastWrittenServiceIdRef.current = newId;
      return;
    }

    params.set("serviceId", newId);
    lastWrittenServiceIdRef.current = newId;

    navigate(
      { pathname: location.pathname, search: `?${params.toString()}` },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup?.id, location.pathname, navigate]);

  return isHydrated;
}
